/**
 * DRI-19: a soil report whose analysis is being recalculated answers a PDF download with
 * 409 + `ApiResponse` failure ("Report being updated"). The download asks for a Blob, so the
 * error interceptor cannot read that body (its 409 branch rejects a bare Error with
 * no status); the download services widen `validateStatus` to 409 and call `readNotReady`.
 *
 * Kept in its own module so page tests that `mock.module` a whole service still see the real class.
 */

/** Neutral wording shown when the 409 body cannot be read (the server always sends this text). */
export const REPORT_NOT_READY_FALLBACK = 'Report being updated';

/** Thrown by a report download when the report is being updated (HTTP 409). */
export class ReportNotReadyError extends Error {
  constructor(message?: string | null) {
    super(message && message.trim() ? message : REPORT_NOT_READY_FALLBACK);
    this.name = 'ReportNotReadyError';
  }
}

/** True when `err` is a not-Ready download outcome. */
export const isReportNotReady = (err: unknown): err is ReportNotReadyError =>
  err instanceof ReportNotReadyError;

const readBodyText = async (data: unknown): Promise<string | null> => {
  if (data == null) return null;
  if (typeof data === 'string') return data;
  if (typeof (data as Blob).text === 'function') return (data as Blob).text();
  if (typeof data === 'object') {
    const message = (data as { message?: unknown }).message;
    return typeof message === 'string' ? JSON.stringify({ message }) : null;
  }
  return null;
};

/**
 * On a 409 response, read the `ApiResponse` failure out of the Blob body and throw
 * `ReportNotReadyError` with its message (fallback when the body does not parse). Any other
 * status returns without throwing.
 */
export const readNotReady = async (response: { status: number; data: unknown }): Promise<void> => {
  if (response.status !== 409) return;
  let message: string | null = null;
  try {
    const text = await readBodyText(response.data);
    if (text) {
      const parsed = JSON.parse(text) as { message?: unknown };
      if (typeof parsed?.message === 'string') message = parsed.message;
    }
  } catch {
    message = null;
  }
  throw new ReportNotReadyError(message);
};
